import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import { toast } from "react-toastify";
import { documentService } from "../../../services/api/documentService";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import MobilePatternHeader from "../components/MobilePatternHeader";
import Button from "../components/Button";
import BackButton from "../components/BackButton";
import SharedEmptyState from "../components/EmptyState";

/**
 * (Khuyến nghị production) Nếu có thể, dùng DOMPurify:
 * import DOMPurify from "dompurify";
 * const sanitizeHTML = (html) => DOMPurify.sanitize(html);
 * Ở đây demo 1 hàm giả-lập ngắn gọn (không đủ an toàn cho production).
 */
const sanitizeHTML = (html = "") => {
  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    // Loại bỏ script + sự kiện inline cơ bản
    doc
      .querySelectorAll("script, iframe, object, embed")
      .forEach((el) => el.remove());
    doc.querySelectorAll("*").forEach((el) => {
      [...el.attributes].forEach((attr) => {
        if (/^on/i.test(attr.name)) el.removeAttribute(attr.name);
        if (
          attr.name === "href" &&
          el
            .getAttribute("href")
            ?.trim()
            .toLowerCase()
            .startsWith("javascript:")
        ) {
          el.removeAttribute("href");
        }
      });
    });
    return doc.body.innerHTML;
  } catch {
    return "";
  }
};

const PageShell = ({ children, isMobile }) => {
  if (isMobile)
    return <div className="h-full w-full bg-[#F2F2F2]">{children}</div>;
  return (
    <div className="bg-white rounded-[16px] border border-[#D2D5DA] p-4 h-full overflow-auto">
      {children}
    </div>
  );
};

const ContentSkeleton = () => (
  <div className="space-y-4">
    <div className="h-6 w-2/5 rounded bg-gray-200 animate-pulse" />
    {Array.from({ length: 12 }).map((_, i) => (
      <div key={i} className="h-4 w-full rounded bg-gray-200 animate-pulse" />
    ))}
  </div>
);

const EmptyState = ({ onBack }) => (
  <SharedEmptyState
    image="/icons/empty-file.svg"
    imageAlt="Không có nội dung"
    imageClassName="size-16 opacity-70"
    description="Không có nội dung để hiển thị."
    action={
      onBack ? (
      <BackButton
        label="Quay lại"
        onClick={onBack}
        className="mt-2"
      />
      ) : null
    }
  />
);

const CTAButton = ({ href, full = false, label = "Tới trang web" }) => {
  if (!href) return null;
  const external = /^https?:\/\//i.test(href);
  return (
    <Button type="button" variant="primary" className="w-full max-w-[400px]">
      <a
        className={`${
          full ? "w-full" : "w-1/2"
        } flex items-center justify-center focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0457C2] outline-none`}
        href={href}
        target={external ? "_blank" : undefined}
        rel={external ? "noopener noreferrer" : undefined}
      >
        {label}
      </a>
    </Button>
  );
};

const useRemoteContent = (content) => {
  const [mode, setMode] = useState("idle"); // 'idle' | 'loading' | 'text' | 'html' | 'pdf' | 'error'
  const [text, setText] = useState("");
  const [pdfBlobUrl, setPdfBlobUrl] = useState("");
  const [contentType, setContentType] = useState("");

  useEffect(() => {
    let cancelled = false;
    let objectUrl = "";

    const load = async () => {
      if (!content) {
        setMode("idle");
        return;
      }

      // Kiểm tra xem content có phải là URL không
      const isUrl = /^https?:\/\//i.test(content);

      if (!isUrl) {
        // Nếu không phải URL, coi như là HTML content trực tiếp
        if (cancelled) return;
        setText(sanitizeHTML(content));
        setMode("html");
        return;
      }

      // Nếu là URL thì fetch
      setMode("loading");
      try {
        const res = await fetch(content);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const ct = (res.headers.get("Content-Type") || "").toLowerCase();
        if (cancelled) return;
        setContentType(ct);

        // Nếu PDF thực sự
        if (ct.includes("application/pdf")) {
          const blob = await res.blob();
          if (cancelled) return;
          objectUrl = URL.createObjectURL(blob);
          setPdfBlobUrl(objectUrl);
          setMode("pdf");
          return;
        }

        // Còn lại coi như văn bản
        const raw = await res.text();
        if (cancelled) return;

        // Thử đoán HTML
        const looksHTML =
          ct.includes("text/html") ||
          /^<(!doctype|html|head|body|div|p|h[1-6]|section)\b/i.test(
            raw.trim(),
          );

        if (looksHTML) {
          setText(sanitizeHTML(raw));
          setMode("html");
        } else {
          setText(raw);
          setMode("text");
        }
      } catch (e) {
        if (cancelled) return;
        setMode("error");
      }
    };

    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [content]);

  return { mode, text, pdfBlobUrl, contentType };
};

const pageVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.18 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.12 } },
};

const DocumentPage = ({ isMobile }) => {
  const navigate = useNavigate();
  const { token_client } = useContext(AuthContext);

  const [doc, setDoc] = useState(null); // {_id, name, pdfUrl, ...}
  const [loading, setLoading] = useState(false);

  // Lấy document đầu tiên theo đúng schema bạn đưa
  const fetchDocument = useCallback(async () => {
    try {
      setLoading(true);
      const res = await documentService.get("", token_client);
      const first = res?.data?.documents?.[0] || null;
      setDoc(first);
    } catch (error) {
      toast.dismiss();
      toast.error(
        error?.response
          ? `Lỗi từ server: ${error.response.data?.message || "Không xác định"}`
          : "Không thể kết nối tới server. Vui lòng kiểm tra mạng.",
      );
      setDoc(null);
    } finally {
      setLoading(false);
    }
  }, [token_client]);

  useEffect(() => {
    fetchDocument();
  }, [fetchDocument]);

  // Đọc nội dung từ pdfUrl (có thể là HTML content trực tiếp hoặc URL)
  const contentUrl = doc?.pdfUrl || ""; // theo response của bạn
  const { mode, text, pdfBlobUrl } = useRemoteContent(contentUrl);

  const ctaHref = useMemo(() => doc?.name || "", [doc]); // mở trang ngoài

  /* MOBILE */
  if (isMobile) {
    return (
      <AnimatePresence mode="wait">
        <motion.div
          key="doc-mobile"
          variants={pageVariants}
          initial="initial"
          animate="animate"
          exit="exit"
          className="h-full w-full bg-[#F2F2F2]"
        >
          <MobilePatternHeader
            title="Mua bán sở hữu trí tuệ"
            onBack={() => navigate(-1)}
          />

          <div className="h-[calc(100%-124px)] p-5">
            <div className="h-[calc(100%-70px)] overflow-auto bg-white rounded-lg p-4">
              {loading || mode === "loading" ? (
                <ContentSkeleton />
              ) : !doc ? (
                <EmptyState onBack={() => navigate(-1)} />
              ) : mode === "pdf" ? (
                <iframe
                  title="PDF Preview"
                  src={pdfBlobUrl}
                  className="w-full h-[70vh] rounded border"
                />
              ) : mode === "html" ? (
                <div
                  className="prose max-w-none prose-p:my-3 prose-li:my-1 prose-headings:mt-6 prose-headings:mb-3 font-sf-regular"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              ) : mode === "text" ? (
                <pre className="whitespace-pre-wrap break-words text-gray-700 text-[15px] leading-relaxed">
                  {text}
                </pre>
              ) : mode === "error" ? (
                <div className="text-red-600">
                  Không thể tải nội dung từ pdfUrl.
                </div>
              ) : (
                <EmptyState onBack={() => navigate(-1)} />
              )}
            </div>

            {/* CTA sticky */}
            <div className="sticky bottom-4 mt-3">
              <CTAButton href={ctaHref} full label="Tới trang web" />
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  /* DESKTOP */
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key="doc-desktop"
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
      >
        <PageShell isMobile={false}>
          {loading || mode === "loading" ? (
            <ContentSkeleton />
          ) : !doc ? (
            <EmptyState onBack={() => navigate(-1)} />
          ) : mode === "pdf" ? (
            <iframe
              title="PDF Preview"
              src={pdfBlobUrl}
              className="w-full h-[80vh] rounded border"
            />
          ) : mode === "html" ? (
            <div
              className="prose max-w-none prose-p:my-3 prose-li:my-1 prose-headings:mt-6 prose-headings:mb-3 font-sf-regular"
              dangerouslySetInnerHTML={{ __html: text }}
            />
          ) : mode === "text" ? (
            <pre className="whitespace-pre-wrap break-words text-gray-700 text-[15px] leading-relaxed">
              {text}
            </pre>
          ) : mode === "error" ? (
            <div className="text-red-600">
              Không thể tải nội dung từ pdfUrl.
            </div>
          ) : (
            <EmptyState onBack={() => navigate(-1)} />
          )}

          <div className="w-full flex justify-center">
            <CTAButton href={ctaHref} label="Tới trang web" />
          </div>
        </PageShell>
      </motion.div>
    </AnimatePresence>
  );
};

export default DocumentPage;
